<template>
    <v-container style="max-width: 960px" class="py-8">
        <h1 class="text-h5 mb-1">UAT Apps</h1>
        <p class="text-body-2 text-medium-emphasis mb-6">
            Internal tools for putting test data through the platform.
        </p>

        <v-alert
            v-if="unavailable"
            type="warning"
            variant="tonal"
            class="mb-6"
            data-testid="unavailable-notice"
        >
            <strong>{{ unavailable }}</strong> is not available for {{ session.tenantLabel.value }}.
            Switch tenant to open it.
        </v-alert>

        <!--
          Reaching this means signed in, with a tenant, and nothing gated to it —
          so it is addressed to a tester, not a developer. The previous copy told
          the reader to add a directory under src/apps/, which was the right
          instruction for the only case that could produce an empty list before
          apps were tenant-gated, and the wrong one afterwards.
        -->
        <v-alert v-if="!visibleEntries.length" type="info" variant="tonal">
            No apps are set up for <strong>{{ session.tenantLabel.value }}</strong
            >. Sign out and choose another tenant, or ask for one to be added.
        </v-alert>

        <v-row v-else dense>
            <v-col v-for="entry in visibleEntries" :key="entry.id" cols="12" sm="6">
                <v-card
                    :to="entry.routePath"
                    class="pa-4 h-100"
                    variant="outlined"
                    :data-testid="`app-card-${entry.id}`"
                >
                    <div class="d-flex align-center mb-2">
                        <v-icon :icon="entry.icon" color="primary" class="mr-3" size="28" />
                        <span class="text-subtitle-1">{{ entry.title }}</span>
                    </div>
                    <p v-if="entry.description" class="text-body-2 text-medium-emphasis mb-0">
                        {{ entry.description }}
                    </p>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
// Derived from the app registry, never hand-maintained: a new directory under
// src/apps/ appears here and in the nav with no edit to this file.
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { entriesForTenant, navEntries } from '../registry';
import { useSession } from '../session/session';

const session = useSession();
const route = useRoute();

const visibleEntries = computed(() => entriesForTenant(navEntries, session.tenantId.value));

// Set by the route guard when someone opens a tenant-gated app they cannot use.
// Without this the redirect would look like a dead link.
const unavailable = computed(() => {
    const id = route.query.unavailable;
    if (typeof id !== 'string') return null;
    return navEntries.find((entry) => entry.id === id)?.title ?? id;
});
</script>
